import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// Types for SODP tickets (will be moved to shared later)
export interface SODPTicket {
  id: string;
  key: string;
  summary: string;
  status: string;
  priority: string;
  assignee?: string;
  created: string;
  updated: string;
  description?: string;
  labels: string[];
}

export interface SODPState {
  tickets: SODPTicket[];
  loading: boolean;
  error: string | null;
  lastFetched: string | null;
  filters: {
    status: string[];
    priority: string[];
    assignee: string[];
  };
}

const initialState: SODPState = {
  tickets: [],
  loading: false,
  error: null,
  lastFetched: null,
  filters: {
    status: [],
    priority: [],
    assignee: [],
  },
};

// Async thunk for fetching SODP tickets (now from the simplified SODP endpoint)
export const fetchSODPTickets = createAsyncThunk(
  'sodp/fetchTickets',
  async (_, { rejectWithValue }) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/sodp/tickets`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Handle the new simplified response format from SODP endpoint
      if (data.tickets && Array.isArray(data.tickets)) {
        return data.tickets as SODPTicket[];
      } else if (data.error) {
        throw new Error(data.error);
      } else {
        throw new Error('Invalid response format from SODP API');
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch SODP tickets');
    }
  }
);

const sodpSlice = createSlice({
  name: 'sodp',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<SODPState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        status: [],
        priority: [],
        assignee: [],
      };
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSODPTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSODPTickets.fulfilled, (state, action) => {
        state.loading = false;
        state.tickets = action.payload;
        state.lastFetched = new Date().toISOString();
        state.error = null;
      })
      .addCase(fetchSODPTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setFilters, clearFilters, clearError } = sodpSlice.actions;
export default sodpSlice.reducer;
